import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  plan_product_pricings_products_path,
  plan_product_pricings_search_products_path,
} from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import ProductPickerTable, { ProductPickerFooter, type PickerProduct } from './ProductPickerTable'

interface ApiPayload {
  page: number
  per_page: number
  total_count: number
  total_pages: number
  months: string[]
  products: PickerProduct[]
}

type AddState = { kind: 'adding' } | { kind: 'added'; nodeName: string } | { kind: 'no_place' }

interface Props {
  slug: string
  channelSlug: string
  channelName: string
  planCurrency?: string | null
  open: boolean
  onClose: () => void
  onAdded: () => void
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
}

// Channel-level add-product picker for the pricings page: searches any org
// product (blank query browses everything, ordered by recent channel
// revenue), and Add places the product under the plan leaf it belongs to —
// the same auto-placement the CSV upload uses.
export default function AddPricingProductModal({ slug, channelSlug, channelName, planCurrency, open, onClose, onAdded }: Props) {
  const market = useMarket()
  const [query, setQuery] = useState('')
  const [data, setData] = useState<ApiPayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [addStates, setAddStates] = useState<Record<string, AddState>>({})
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchPage = useCallback(
    (targetPage: number, q: string) => {
      setLoading(true)
      const base = plan_product_pricings_search_products_path(market.slug, slug, channelSlug)
      fetch(`${base}?q=${encodeURIComponent(q)}&page=${targetPage}`, { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
        .then((json: ApiPayload) => setData(json))
        .catch(() => setData(null))
        .finally(() => setLoading(false))
    },
    [market.slug, slug, channelSlug],
  )

  useEffect(() => {
    if (!open) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchPage(1, query), data === null ? 0 : 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only on open/query changes
  }, [open, query, fetchPage])

  const addProduct = useCallback(
    (p: PickerProduct) => {
      setAddStates((s) => ({ ...s, [p.external_id]: { kind: 'adding' } }))
      fetch(plan_product_pricings_products_path(market.slug, slug, channelSlug), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
        body: JSON.stringify({ product_external_id: p.external_id }),
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
        .then((result: { added: boolean; node_name?: string; reason?: string }) => {
          if (result.added) {
            setAddStates((s) => ({ ...s, [p.external_id]: { kind: 'added', nodeName: result.node_name ?? '' } }))
            onAdded()
          } else if (result.reason === 'already_placed') {
            setAddStates((s) => {
              const next = { ...s }
              delete next[p.external_id]
              return next
            })
            setData((prev) =>
              prev
                ? { ...prev, products: prev.products.map((x) => (x.external_id === p.external_id ? { ...x, in_plan: true } : x)) }
                : prev,
            )
          } else {
            setAddStates((s) => ({ ...s, [p.external_id]: { kind: 'no_place' } }))
          }
        })
        .catch(() =>
          setAddStates((s) => {
            const next = { ...s }
            delete next[p.external_id]
            return next
          }),
        )
    },
    [market.slug, slug, channelSlug, onAdded],
  )

  const renderAction = (p: PickerProduct) => {
    const state = addStates[p.external_id]
    if (state?.kind === 'added') {
      return (
        <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
          Added to {state.nodeName}
        </span>
      )
    }
    if (state?.kind === 'no_place') {
      return (
        <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
          No place in this plan's tree
        </span>
      )
    }
    if (p.in_plan) {
      return (
        <span
          title={p.placed_node_name ? `Currently in ${p.placed_node_name}` : undefined}
          className="max-w-[100px] truncate text-[10px] inline-block"
          style={{ color: 'var(--muted-foreground)' }}
        >
          In {p.placed_node_name ?? 'plan'}
        </span>
      )
    }
    return (
      <Button variant="outline" size="sm" disabled={state?.kind === 'adding'} onClick={() => addProduct(p)} className="h-7 w-[78px]">
        {state?.kind === 'adding' ? 'Adding…' : 'Add'}
      </Button>
    )
  }

  function close() {
    setQuery('')
    setData(null)
    setAddStates({})
    onClose()
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'color-mix(in srgb, var(--foreground) 30%, transparent)' }}
      onClick={close}
    >
      <div
        style={{ background: 'var(--background)', borderColor: 'var(--border)' }}
        className="w-[1280px] max-w-[80vw] max-h-[80vh] rounded-md border shadow-md overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b flex items-start justify-between gap-4" style={{ borderColor: 'var(--border)' }}>
          <div className="min-w-0">
            <div style={{ color: 'var(--muted-foreground)' }} className="text-[10px] uppercase tracking-wider">
              Add product to
            </div>
            <div style={{ color: 'var(--foreground)' }} className="text-sm font-semibold mt-0.5">
              {channelName}
            </div>
            <div style={{ color: 'var(--muted-foreground)' }} className="text-xs mt-1">
              Each product lands under the plan node it belongs to.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or ID…"
              className="w-64"
              autoFocus
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={close}
              aria-label="Close"
              className="h-7 w-7 -mr-1 text-muted-foreground hover:text-foreground"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </Button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto">
          <ProductPickerTable
            months={data?.months ?? null}
            products={data?.products ?? null}
            channelName={channelName}
            planCurrency={planCurrency}
            loading={loading}
            emptyMessage={query.trim() === '' ? 'No products.' : 'No products match your search.'}
            renderAction={renderAction}
          />
        </div>

        <ProductPickerFooter
          page={data?.page ?? 1}
          totalPages={data?.total_pages ?? 1}
          totalCount={data?.total_count ?? 0}
          loaded={data !== null}
          loading={loading}
          onPageChange={(p) => fetchPage(p, query)}
        />
      </div>
    </div>
  )
}
